import { KnopClient } from "@opstel/query/knopClient";
import { KnopHttpClient } from "@opstel/query/knopHttpClient";
import {
  PersoneelsdienstSchedulerJob,
  usePersoneelsdienstSchedulerStore,
} from "@personeel/stores/PersoneelsdienstSchedulerStore";
import { config } from "@shared/envVariables";
import { useMessageBannerStore } from "@shared/shared-zustand/MessageBannerStore";
import { QueryClient } from "@tanstack/react-query";

const MAX_POLLING_MS = 15 * 60 * 1000;
const POLL_INTERVAL_MS = 5000;
const MAX_CONSECUTIVE_POLL_ERRORS = 3;
const POLLING_STATE_BUSY = "BEZIG";
const POLLING_STATE_READY = "KLAAR";

type StartPersoneelsdienstSchedulerPollingParams = PersoneelsdienstSchedulerJob & {
  knopClient: KnopClient;
  queryClient: QueryClient;
  instanceId: string;
};

let bannerIntervalId: ReturnType<typeof setInterval> | null = null;
let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;
let activePollingSessionId = 0;
let isStoreSubscriptionActive = false;

function stopPollingTimers() {
  if (bannerIntervalId !== null) {
    clearInterval(bannerIntervalId);
    bannerIntervalId = null;
  }

  if (pollTimeoutId !== null) {
    clearTimeout(pollTimeoutId);
    pollTimeoutId = null;
  }
}

function createPollingSessionId() {
  activePollingSessionId += 1;
  return activePollingSessionId;
}

function ensureStoreSubscription() {
  if (isStoreSubscriptionActive) {
    return;
  }

  usePersoneelsdienstSchedulerStore.subscribe((state) => {
    if (!state.activeJob) {
      stopPollingTimers();
    }
  });
  isStoreSubscriptionActive = true;
}

function isCurrentPollingSession(pollingSessionId: number, ticket: string) {
  const activeJob = usePersoneelsdienstSchedulerStore.getState().activeJob;
  return activePollingSessionId === pollingSessionId && activeJob?.ticket === ticket;
}

async function fetchSchedulerStatus(
  ticket: string,
  knopClient: KnopClient,
): Promise<{ status: string }> {
  const url = (config.personeel?.apiBaseUrl ?? "") + `/v1/ps-jobs/${ticket}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  const token = await (knopClient as KnopHttpClient).getToken();
  let response: Response;

  if (!token) {
    throw new Error("Token is invalid");
  }

  try {
    response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("Request timeout");
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as { status: string };
}

/** @internal Exposed for cleanup and testing purposes. */
export function stopPersoneelsdienstSchedulerPolling() {
  stopPollingTimers();
  createPollingSessionId();
}

export function startPersoneelsdienstSchedulerPolling({
  ticket,
  startedAtMs,
  periodLabel,
  knopClient,
  queryClient,
  instanceId,
}: StartPersoneelsdienstSchedulerPollingParams) {
  ensureStoreSubscription();
  stopPollingTimers();
  const pollingSessionId = createPollingSessionId();
  const setBanner = useMessageBannerStore.getState().setBanner;
  let consecutivePollErrors = 0;

  usePersoneelsdienstSchedulerStore.getState().startJob({
    ticket,
    startedAtMs,
    periodLabel,
  });

  setBanner(`Automatisch toewijzen klussen (${periodLabel}) is 00:00 bezig.`, "info");

  const scheduleNextPoll = () => {
    pollTimeoutId = setTimeout(() => {
      pollSchedulerStatus().catch(() => undefined);
    }, POLL_INTERVAL_MS);
  };

  const pollSchedulerStatus = async () => {
    if (!isCurrentPollingSession(pollingSessionId, ticket)) {
      return;
    }

    try {
      const result = await fetchSchedulerStatus(ticket, knopClient);

      if (!isCurrentPollingSession(pollingSessionId, ticket)) {
        return;
      }

      consecutivePollErrors = 0;

      if (result.status === POLLING_STATE_BUSY) {
        scheduleNextPoll();
        return;
      }

      if (result.status === POLLING_STATE_READY) {
        setBanner("Automatisch toewijzen klussen is gereed.", "success");
        usePersoneelsdienstSchedulerStore.getState().clearJob();

        try {
          await queryClient.refetchQueries({
            queryKey: ["klussenOverview", instanceId],
            exact: true,
            type: "all",
          });
        } catch {
          // Keep the successful completion state; a refetch failure should not mark polling as failed.
        }

        return;
      }

      setBanner(
        `Automatisch toewijzen van klussen is gestopt met status ${result.status}.`,
        "error",
      );
      usePersoneelsdienstSchedulerStore.getState().clearJob();
    } catch {
      if (!isCurrentPollingSession(pollingSessionId, ticket)) {
        return;
      }

      consecutivePollErrors += 1;

      if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        setBanner(
          "Automatisch toewijzen van klussen is mislukt. De status kon niet meer worden opgehaald.",
          "error",
        );
        usePersoneelsdienstSchedulerStore.getState().clearJob();
        return;
      }

      scheduleNextPoll();
    }
  };

  bannerIntervalId = setInterval(() => {
    if (!isCurrentPollingSession(pollingSessionId, ticket)) {
      return;
    }

    const elapsedMs = Date.now() - startedAtMs;

    if (elapsedMs >= MAX_POLLING_MS) {
      setBanner(
        "Automatisch toewijzen van klussen is mislukt. De aanvraag duurde langer dan 15 minuten.",
        "error",
      );
      usePersoneelsdienstSchedulerStore.getState().clearJob();
      return;
    }

    const elapsedTotalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const minutes = String(Math.floor(elapsedTotalSeconds / 60)).padStart(2, "0");
    const seconds = String(elapsedTotalSeconds % 60).padStart(2, "0");
    setBanner(
      `Automatisch toewijzen klussen (${periodLabel}) is ${minutes}:${seconds} bezig.`,
      "info",
    );
  }, 1000);

  scheduleNextPoll();
}
